import React, { memo, useCallback, useMemo, useState } from 'react';
import {
    Grid,
    Checkbox,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Box,
    TableContainer,
    TablePagination,
    CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface Establishment {
    id: number;
    codigoSRT: string;
    nombre: string;
    contrato: string;
    ciiuCodigo?: string;
    ciiuNombre?: string;
    localidad: any;
    postalCode: string;
    estado: number;
    direccion: any;
}

interface Props {
    establishments: Establishment[];
    selectedEstablishments: number[];
    handleSelectEstablishment: (id: number) => void;
    handleSelectEstablishments: (ids: number[], selected: boolean) => void;
    handleClearSelection: () => void;
    isLoading?: boolean;
    page: number;
    rowsPerPage: number;
    totalCount: number;
    onChangePage: (newPage: number) => void;
    onChangeRowsPerPage: (newRowsPerPage: number) => void;
}

// Anchos (en %) compartidos entre la tabla de encabezado (fuera del scroll) y la de filas
// (dentro del scroll), para que las columnas queden alineadas al separarlas en dos <Table>
// y ambas puedan ocupar el 100% del ancho disponible.
const COLUMN_WIDTHS = ['4%', '7%', '15%', '16%', '24%', '15%', '19%'];

const formatDireccion = (direccion?: any): string => {
    if (!direccion) return 'N/A';

    const { calle, altura, departamento } = direccion;
    const calleYAltura = calle && altura ? `${calle} ${altura}` : calle || altura || '';
    const dpto = departamento ? ` - Dpto: ${departamento}` : '';

    return calleYAltura + dpto || 'N/A';
};

const EstablishmentRow = memo(
    ({
        establishment,
        checked,
        onSelect
    }: {
        establishment: Establishment;
        checked: boolean;
        onSelect: (id: number) => void;
    }) => (
        <TableRow>
            <TableCell style={{ width: COLUMN_WIDTHS[0] }}>
                <Checkbox checked={checked} onChange={() => onSelect(establishment.id)} />
            </TableCell>
            <TableCell style={{ width: COLUMN_WIDTHS[1] }}>{establishment.codigoSRT}</TableCell>
            <TableCell style={{ width: COLUMN_WIDTHS[2] }}>{establishment.nombre}</TableCell>
            <TableCell style={{ width: COLUMN_WIDTHS[3] }}>{establishment.contrato}</TableCell>
            <TableCell style={{ width: COLUMN_WIDTHS[4] }}>
                {establishment.ciiuCodigo} {establishment.ciiuNombre} ||{' '}
            </TableCell>

            <TableCell style={{ width: COLUMN_WIDTHS[5] }}>
                {formatDireccion(establishment.direccion)}
            </TableCell>
            <TableCell style={{ width: COLUMN_WIDTHS[6] }}>
                {establishment?.localidad?.nombre || establishment?.localidad || 'N/A'} -{' '}
                {establishment?.localidad?.codigoPostal || establishment?.postalCode || 'N/A'}
            </TableCell>
        </TableRow>
    )
);

const EstablecimientosSelector: React.FC<Props> = ({
    establishments,
    selectedEstablishments,
    handleSelectEstablishment,
    handleSelectEstablishments,
    isLoading,
    page,
    rowsPerPage,
    totalCount,
    onChangePage,
    onChangeRowsPerPage
}) => {
    const { t } = useTranslation('visitas');
    const { t: tCommon } = useTranslation('common');
    const [searchTerm, setSearchTerm] = useState('');
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const selectedEstablishmentsSet = useMemo(
        () => new Set(selectedEstablishments),
        [selectedEstablishments]
    );

    const validEstablishments = useMemo(
        () => establishments.filter((est: any) => est.estado !== 3 && est.estado !== 4),
        [establishments]
    );

    const filteredEstablishments = useMemo(() => {
        if (searchTerm.length < 3) return validEstablishments;

        const term = searchTerm.toLowerCase();

        return validEstablishments.filter((est) => {
            const nombre = est.nombre?.toLowerCase() ?? '';
            const calle = est.direccion?.calle?.toLowerCase() ?? '';
            const altura = est.direccion?.altura?.toLowerCase() ?? '';
            const departamento = est.direccion?.departamento?.toLowerCase() ?? '';

            return (
                nombre.includes(term) ||
                calle.includes(term) ||
                altura.includes(term) ||
                departamento.includes(term)
            );
        });
    }, [searchTerm, validEstablishments]);

    // El paginador trae los establecimientos de a una página (backend-paginado), así que
    // "marcar todo" solo afecta lo que se muestra en la página actual, igual que en la
    // grilla RGRL de Gestión de Archivos SRT.
    const pageEstablishmentIds = useMemo(
        () => filteredEstablishments.map((est) => est.id),
        [filteredEstablishments]
    );

    const isAllPageSelected =
        pageEstablishmentIds.length > 0 &&
        pageEstablishmentIds.every((id) => selectedEstablishmentsSet.has(id));

    const handleSelectAllPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        handleSelectEstablishments(pageEstablishmentIds, event.target.checked);
    };

    const handleSelect = useCallback(
        (id: number) => {
            handleSelectEstablishment(id);
        },
        [handleSelectEstablishment]
    );

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Paper elevation={3}>
                        <Box
                            sx={{
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                overflow: 'hidden'
                            }}
                        >
                            {/* Encabezado en su propia tabla, fuera del contenedor con scroll,
                                para que la barra de scroll del body no se solape con el header. */}
                            <TableContainer sx={{ overflowX: 'auto' }}>
                                <Table
                                    size="medium"
                                    sx={{ tableLayout: 'fixed', width: '100%' }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                padding="checkbox"
                                                style={{ width: COLUMN_WIDTHS[0] }}
                                            >
                                                <Checkbox
                                                    checked={isAllPageSelected}
                                                    onChange={handleSelectAllPageChange}
                                                    inputProps={{
                                                        'aria-label': t(
                                                            'buttons.selectAllEstablishments'
                                                        ) as string
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell style={{ width: COLUMN_WIDTHS[1] }}>
                                                {t('table.columnsTitles.srtCode')}
                                            </TableCell>
                                            <TableCell style={{ width: COLUMN_WIDTHS[2] }}>
                                                {t('table.columnsTitles.name')}
                                            </TableCell>
                                            <TableCell style={{ width: COLUMN_WIDTHS[3] }}>
                                                {t('table.columnsTitles.contract')}
                                            </TableCell>
                                            <TableCell style={{ width: COLUMN_WIDTHS[4] }}>
                                                {t('table.columnsTitles.ciiu')}
                                            </TableCell>
                                            <TableCell style={{ width: COLUMN_WIDTHS[5] }}>
                                                {t('table.columnsTitles.address')}
                                            </TableCell>
                                            <TableCell style={{ width: COLUMN_WIDTHS[6] }}>
                                                {t('table.columnsTitles.city')} -{' '}
                                                {t('table.columnsTitles.postalCode')}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                </Table>
                            </TableContainer>

                            <TableContainer
                                sx={{
                                    maxHeight: '400px',
                                    overflow: 'auto'
                                }}
                            >
                                <Table
                                    size="medium"
                                    sx={{
                                        tableLayout: 'fixed',
                                        width: '100%',
                                        '& td': { borderBottom: '1px solid #e0e0e0' }
                                    }}
                                >
                                    {isLoading ? (
                                        <TableBody>
                                            <TableRow>
                                                <TableCell colSpan={7}>
                                                    <Box
                                                        display="flex"
                                                        justifyContent="center"
                                                        alignItems="center"
                                                        sx={{ py: 5 }}
                                                    >
                                                        <CircularProgress size={32} />
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    ) : (
                                        <TableBody>
                                            {filteredEstablishments.length > 0 ? (
                                                filteredEstablishments.map((est: any) => (
                                                    <EstablishmentRow
                                                        key={est.id}
                                                        establishment={est}
                                                        checked={selectedEstablishmentsSet.has(
                                                            est.id
                                                        )}
                                                        onSelect={handleSelect}
                                                    />
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={7}>
                                                        <Typography
                                                            variant="body2"
                                                            align="center"
                                                            sx={{ py: 2 }}
                                                        >
                                                            {t(
                                                                'table.noData.noEstablishmentsWithFilters'
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    )}
                                </Table>
                            </TableContainer>
                        </Box>
                        {totalCount > 0 && (
                            <TablePagination
                                rowsPerPageOptions={[500, 1000, 2000, 5000]}
                                component="div"
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={totalCount <= rowsPerPage ? 0 : page}
                                onPageChange={(_, newPage) => onChangePage(newPage)}
                                onRowsPerPageChange={(event) =>
                                    onChangeRowsPerPage(parseInt(event.target.value, 10))
                                }
                                labelRowsPerPage={tCommon('table.pagination.rowsPerPage')}
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from}–${to} ${tCommon('table.pagination.of')} ${count}`
                                }
                            />
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </>
    );
};

export default EstablecimientosSelector;
